import { apiClient, requestDeduplicator, responseCache } from '../apiClient';
import { EnhancedArtifact } from '../../types/artifacts';

export interface ArtifactListResponse {
  artifacts: EnhancedArtifact[];
  total: number;
  page: number;
  limit: number;
}

export interface ArtifactCreateRequest {
  name: string;
  description?: string;
  artifact_type_id: string;
  artifact_type?: string; // Artifact type name (may be required by backend)
  artifact_tree_id?: string;
  parent_ref?: string;
  business_model_id?: string;
  company_id?: string; // Company ID (companies.id)
  artifact_metadata?: Record<string, any>;
}

export interface ArtifactUpdateRequest {
  name?: string;
  description?: string;
  artifact_type_id?: string;
  parent_ref?: string;
  prime_artifact_id?: string | null;
  business_model_id?: string | null;
  artifact_metadata?: Record<string, any>;
}

class ArtifactService {
  private static artifactCache: Map<string, EnhancedArtifact> = new Map();
  private static parentCache: Map<string, EnhancedArtifact[]> = new Map();
  private static cacheTimestamp: number | null = null;
  private static readonly CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

  private static isCacheValid(): boolean {
    return this.cacheTimestamp !== null && (Date.now() - this.cacheTimestamp < this.CACHE_DURATION);
  }

  static async getArtifacts(
    modelType?: 'prime' | 'company',
    artifactTreeId?: string,
    primeArtifactId?: string,
    companyId?: string
  ): Promise<EnhancedArtifact[]> {
    try {
      console.log('🔄 [ArtifactService] Fetching artifacts...', { modelType, artifactTreeId, primeArtifactId });
      
      // Fetch from API; no mock data in production flow
      let artifacts: EnhancedArtifact[] = [];
      if (artifactTreeId) {
        this.cacheTimestamp = null;
      }
      
      {
        try {
          let url = '/artifacts';
          const params = new URLSearchParams();
          
          if (artifactTreeId) {
            params.append('tree_id', artifactTreeId);
          }
          if (companyId) {
            params.append('company_id', companyId);
          }
          
          if (params.toString()) {
            url += `?${params.toString()}`;
          }
          
          console.log('📡 [ArtifactService] Calling artifacts endpoint:', url);
          const response = await apiClient.get(url);
          console.log('📥 [ArtifactService] Artifacts API response:', {
            status: response.status,
            dataType: typeof response.data,
            isArray: Array.isArray(response.data),
            dataKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : 'N/A',
            dataLength: Array.isArray(response.data) ? response.data.length : (response.data?.artifacts?.length || response.data?.items?.length || 'N/A'),
            sampleData: Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : (response.data?.artifacts?.[0] || response.data?.items?.[0] || 'N/A')
          });
          
          // Check if response is an error object
          if (response.data && (response.data.detail || response.data.error || response.data.message)) {
            console.warn('⚠️ [ArtifactService] API returned error response:', response.data);
            // Don't treat error responses as artifacts
            artifacts = [];
          } else {
            // Handle different response formats
            if (Array.isArray(response.data)) {
              artifacts = response.data;
            } else if (response.data && Array.isArray(response.data.artifacts)) {
              artifacts = response.data.artifacts;
            } else if (response.data && Array.isArray(response.data.items)) {
              artifacts = response.data.items;
            } else {
              console.warn('⚠️ [ArtifactService] Unexpected API response format:', response.data);
              artifacts = [];
            }
            console.log('✅ [ArtifactService] Fetched artifacts from API:', artifacts.length);
          }
        } catch (apiError: any) {
          // Handle API errors gracefully
          console.error('❌ [ArtifactService] Failed to fetch artifacts from API:', {
            status: apiError.response?.status,
            message: apiError.message,
            data: apiError.response?.data
          });
          // Return empty array on error (don't throw, allow UI to continue)
          artifacts = [];
        }
      }
      
      // Cache the artifacts
      artifacts.forEach((artifact: EnhancedArtifact) => this.artifactCache.set(artifact.id, artifact));
      this.cacheTimestamp = Date.now();
      
      return artifacts;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to fetch artifacts:', error);
      return [];
    }
  }

  static async getArtifactById(id: string): Promise<EnhancedArtifact | null> {
    // Check cache first
    if (this.artifactCache.has(id) && this.isCacheValid()) {
      return this.artifactCache.get(id)!;
    }

    try {
      console.log('🔄 [ArtifactService] Fetching artifact by ID (API first):', id);

      // Try API first for authoritative data
      try {
        const response = await apiClient.get(`/artifacts/${id}`);
        const artifact = response.data;
        if (artifact) {
          console.log('✅ [ArtifactService] Fetched artifact by ID from API:', artifact.name);
          this.artifactCache.set(id, artifact);
          this.cacheTimestamp = Date.now();
          return artifact;
        }
      } catch (apiError: any) {
        console.warn('⚠️ [ArtifactService] API fetch by ID failed:', {
          status: apiError.response?.status,
          message: apiError.message,
        });
      }

      // No mock fallback: return null when API fails
      return null;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to fetch artifact by ID:', id, error);
      return null;
    }
  }

  static async updateArtifact(id: string, updates: ArtifactUpdateRequest): Promise<EnhancedArtifact | null> {
    try {
      console.log('🔄 [ArtifactService] Updating artifact:', id, updates);
      
      // Clean up the updates - remove undefined values, exclude 'id' field, and ensure null values are sent for clearing fields
      const cleanedUpdates: Record<string, any> = {};
      Object.keys(updates).forEach((key) => {
        // Skip 'id' field - it's in the URL path, not the request body
        if (key === 'id') {
          return;
        }
        
        const value = updates[key as keyof ArtifactUpdateRequest];
        // Include the field if it's not undefined (null is valid for clearing fields)
        // Always include artifact_type_id and prime_artifact_id if they're in the updates
        if (value !== undefined || key === 'artifact_type_id' || key === 'prime_artifact_id') {
          // For artifact_type_id and prime_artifact_id, include them even if undefined to ensure they're sent
          if (key === 'artifact_type_id' || key === 'prime_artifact_id') {
            cleanedUpdates[key] = value !== undefined ? value : null;
          } else {
            cleanedUpdates[key] = value;
          }
        }
      });
      
      console.log('🔄 [ArtifactService] Sending update payload (excluding id):', JSON.stringify(cleanedUpdates, null, 2));
      console.log('🔄 [ArtifactService] Fields in payload:', Object.keys(cleanedUpdates));
      console.log('🔄 [ArtifactService] artifact_type_id:', cleanedUpdates.artifact_type_id, '(type:', typeof cleanedUpdates.artifact_type_id, ')');
      console.log('🔄 [ArtifactService] prime_artifact_id:', cleanedUpdates.prime_artifact_id, '(type:', typeof cleanedUpdates.prime_artifact_id, ')');
      
      const response = await apiClient.put(`/artifacts/${id}`, cleanedUpdates);
      const updatedArtifact = response.data;
      
      console.log('✅ [ArtifactService] Update response status:', response.status);
      console.log('✅ [ArtifactService] Update response data:', JSON.stringify(updatedArtifact, null, 2));
      console.log('✅ [ArtifactService] Updated fields - artifact_type_id:', updatedArtifact.artifact_type_id, 'prime_artifact_id:', updatedArtifact.prime_artifact_id);
      
      // Check if the update actually worked
      if (cleanedUpdates.artifact_type_id && String(updatedArtifact.artifact_type_id) !== String(cleanedUpdates.artifact_type_id)) {
        console.error('❌ [ArtifactService] artifact_type_id NOT UPDATED! Sent:', cleanedUpdates.artifact_type_id, 'Received:', updatedArtifact.artifact_type_id);
        // This is a backend issue - the field was sent but not updated
      }
      if (cleanedUpdates.prime_artifact_id !== undefined && cleanedUpdates.prime_artifact_id !== null) {
        const sentId = String(cleanedUpdates.prime_artifact_id);
        const receivedId = updatedArtifact.prime_artifact_id ? String(updatedArtifact.prime_artifact_id) : null;
        if (sentId !== receivedId) {
          console.error('❌ [ArtifactService] prime_artifact_id NOT UPDATED! Sent:', cleanedUpdates.prime_artifact_id, 'Received:', updatedArtifact.prime_artifact_id);
          // This is a backend issue - the field was sent but not updated
        }
      } else if (cleanedUpdates.prime_artifact_id === null && updatedArtifact.prime_artifact_id !== null) {
        console.error('❌ [ArtifactService] prime_artifact_id NOT CLEARED! Sent: null, Received:', updatedArtifact.prime_artifact_id);
      }
      
      // Update cache
      this.artifactCache.set(id, updatedArtifact);
      this.clearCache(); // Clear cache to ensure fresh data on next fetch
      console.log('✅ [ArtifactService] Updated artifact:', updatedArtifact.name);
      return updatedArtifact;
    } catch (error: any) {
      console.error('❌ [ArtifactService] Failed to update artifact:', error);
      console.error('❌ [ArtifactService] Error response:', error.response?.data);
      throw error; // Re-throw so the caller can handle it
    }
  }

  static async createArtifact(artifactData: ArtifactCreateRequest): Promise<EnhancedArtifact | null> {
    try {
      // Clean up the data: convert empty strings to undefined for optional fields
      const cleanedData: ArtifactCreateRequest = {
        name: artifactData.name,
        artifact_type_id: artifactData.artifact_type_id,
        ...(artifactData.artifact_type ? { artifact_type: artifactData.artifact_type } : {}),
        ...(artifactData.description ? { description: artifactData.description } : {}),
        ...(artifactData.artifact_tree_id && artifactData.artifact_tree_id.trim() !== '' 
          ? { artifact_tree_id: artifactData.artifact_tree_id } 
          : {}),
        ...(artifactData.parent_ref && artifactData.parent_ref.trim() !== '' 
          ? { parent_ref: artifactData.parent_ref } 
          : {}),
        ...(artifactData.business_model_id && artifactData.business_model_id.trim() !== '' 
          ? { business_model_id: artifactData.business_model_id } 
          : {}),
        ...(artifactData.company_id != null && String(artifactData.company_id).trim() !== '' 
          ? { company_id: String(artifactData.company_id).trim() } 
          : {}),
        ...(artifactData.artifact_metadata ? { artifact_metadata: artifactData.artifact_metadata } : {}),
      };
      
      console.log('🔄 [ArtifactService] Creating artifact:', cleanedData);
      console.log('🔄 [ArtifactService] Original data had company_id:', artifactData.company_id);
      console.log('🔄 [ArtifactService] Cleaned data has company_id:', cleanedData.company_id);
      console.log('🔄 [ArtifactService] Full cleaned data JSON:', JSON.stringify(cleanedData, null, 2));
      
      const response = await apiClient.post('/artifacts', cleanedData);
      const newArtifact = response.data;
      
      this.artifactCache.set(newArtifact.id, newArtifact);
      this.clearCache(); // Clear cache to ensure fresh data on next fetch
      console.log('✅ [ArtifactService] Created artifact:', newArtifact.name);
      return newArtifact;
    } catch (error: any) {
      // Log detailed error information for debugging
      if (error.response?.data) {
        const errorData = error.response.data;
        const errorDetail = errorData.detail;
        
        // Log the full error response
        console.error('❌ [ArtifactService] Failed to create artifact:', {
          status: error.response.status,
          statusText: error.response.statusText,
          detail: errorDetail,
          fullData: errorData
        });
        
        // Log validation errors if present (array format)
        if (Array.isArray(errorDetail)) {
          console.error('❌ [ArtifactService] Validation Errors:');
          errorDetail.forEach((err: any, index: number) => {
            console.error(`  Error ${index + 1}:`, {
              field: err.loc?.join('.') || 'unknown',
              message: err.msg || err.message || 'No message',
              type: err.type || 'unknown'
            });
          });
        } else if (typeof errorDetail === 'string') {
          // Single error message
          console.error('❌ [ArtifactService] Error message:', errorDetail);
        } else if (errorDetail && typeof errorDetail === 'object') {
          // Error object
          console.error('❌ [ArtifactService] Error object:', JSON.stringify(errorDetail, null, 2));
        }
      } else {
        console.error('❌ [ArtifactService] Failed to create artifact (no response data):', {
          message: error.message,
          code: error.code,
          error: error
        });
      }
      throw error; // Re-throw so the caller can handle it
    }
  }

  // Get potential parent artifacts (excluding the current artifact and its descendants)
  static async getPotentialParents(currentArtifactId?: string): Promise<EnhancedArtifact[]> {
    try {
      const allArtifacts = await this.getArtifacts();
      
      // Filter out the current artifact and its descendants
      const potentialParents = allArtifacts.filter(artifact => {
        if (artifact.id === currentArtifactId) {
          return false;
        }
        
        // TODO: Add logic to exclude descendants
        // For now, just exclude the current artifact
        return true;
      });

      return potentialParents;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to fetch potential parents:', error);
      return [];
    }
  }

  // Resolve parent artifact by ID
  static async resolveParentArtifact(parentRef: string): Promise<EnhancedArtifact | null> {
    if (!parentRef) return null;

    try {
      // Check cache first
      if (this.artifactCache.has(parentRef)) {
        return this.artifactCache.get(parentRef) || null;
      }

      const parent = await this.getArtifactById(parentRef);
      if (parent) {
        this.artifactCache.set(parentRef, parent);
      }

      return parent;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to resolve parent artifact:', error);
      return null;
    }
  }

  // Get artifact type name by ID
  static async getArtifactTypeName(typeId: string): Promise<string> {
    try {
      // TODO: Use artifactTypeService when available
      // For now, return a fallback
      return typeId || 'Unknown Type';
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to get artifact type name:', error);
      return 'Unknown Type';
    }
  }

  static clearCache(): void {
    this.artifactCache.clear();
    this.parentCache.clear();
    this.cacheTimestamp = null;
  }

  // Legacy method compatibility - map to new method names
  static async getById(id: string): Promise<EnhancedArtifact | null> {
    return this.getArtifactById(id);
  }

  static async getAll(treeId?: string, businessModelId?: string, companyId?: string): Promise<EnhancedArtifact[]> {
    return this.getArtifacts('company', treeId, undefined, companyId);
  }

  /**
   * Get artifact hierarchy in nested tree structure
   * Uses the /api/v1/artifacts/hierarchy endpoint which returns nested structure with children arrays
   * ISSUE-066 fix: Uses request deduplication and response caching to prevent redundant API calls
   * @param companyId Optional company ID to filter by
   * @param treeId Optional artifact tree ID to filter by
   * @returns Array of root artifacts with nested children structure
   */
  static async getHierarchy(companyId?: string, treeId?: string): Promise<EnhancedArtifact[]> {
    const params = new URLSearchParams();
    if (companyId) params.append('company_id', companyId);
    if (treeId) params.append('tree_id', treeId);
    
    const url = `/artifacts/hierarchy${params.toString() ? `?${params.toString()}` : ''}`;
    const cacheKey = `hierarchy:${url}`;
    
    // Check cache first (ISSUE-066 fix)
    const cached = responseCache.get(cacheKey);
    if (cached) {
      if (process.env.NODE_ENV === 'development') {
        console.log('📦 [ArtifactService] Using cached hierarchy:', { companyId, treeId, rootCount: cached.length });
      }
      return cached;
    }
    
    // Use request deduplication (ISSUE-066 fix)
    const requestKey = `GET:${url}`;
    
    try {
      const hierarchy = await requestDeduplicator.deduplicate(requestKey, async () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 [ArtifactService] Fetching artifact hierarchy...', { companyId, treeId });
          console.log('📡 [ArtifactService] Calling hierarchy endpoint:', url);
        }
        
        const response = await apiClient.get(url);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('📥 [ArtifactService] Hierarchy API response:', {
            status: response.status,
            dataType: typeof response.data,
            isArray: Array.isArray(response.data),
            dataLength: Array.isArray(response.data) ? response.data.length : 'N/A',
            sampleRoot: Array.isArray(response.data) && response.data.length > 0 ? {
              id: response.data[0].id,
              name: response.data[0].name,
              childrenCount: response.data[0].children?.length || 0
            } : 'N/A'
          });
        }
        
        // Response format: { hierarchy: [...], total_count: number, root_count: number }
        const hierarchyData = response.data.hierarchy || [];
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [ArtifactService] Fetched hierarchy:', {
            rootCount: hierarchyData.length,
            totalCount: response.data.total_count,
          });
        }
        
        return hierarchyData;
      });
      
      // Cache the result (ISSUE-066 fix)
      responseCache.set(cacheKey, hierarchy);
      
      return hierarchy;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to fetch hierarchy:', error);
      return [];
    }
  }

  static async create(data: any): Promise<EnhancedArtifact | null> {
    return this.createArtifact(data);
  }

  static async update(id: string, data: any): Promise<EnhancedArtifact | null> {
    return this.updateArtifact(id, data);
  }

  static async delete(id: string): Promise<void> {
    try {
      console.log('🗑️ [ArtifactService] Deleting artifact:', id);
      await apiClient.delete(`/artifacts/${id}`);
      
      // Remove from cache
      this.artifactCache.delete(id);
      console.log('✅ [ArtifactService] Deleted artifact:', id);
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to delete artifact:', error);
      throw error;
    }
  }

  static async getStates(artifactId: string): Promise<any[]> {
    try {
      console.log('🔄 [ArtifactService] Fetching states for artifact:', artifactId);
      
      const response = await apiClient.get(`/artifacts/${artifactId}/states`);
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to fetch states:', error);
      return [];
    }
  }

  static async getParameters(artifactId: string): Promise<any[]> {
    try {
      console.log('🔄 [ArtifactService] Fetching parameters for artifact:', artifactId);
      
      const response = await apiClient.get(`/artifacts/${artifactId}/parameters`);
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to fetch parameters:', error);
      return [];
    }
  }

  static async createParameter(artifactId: string, data: any): Promise<any> {
    try {
      console.log('🔄 [ArtifactService] Creating parameter for artifact:', artifactId, data);
      
      const response = await apiClient.post(`/artifacts/${artifactId}/parameters`, data);
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to create parameter:', error);
      throw error;
    }
  }

  static async updateParameter(artifactId: string, parameterId: string, data: any): Promise<any> {
    try {
      console.log('🔄 [ArtifactService] Updating parameter:', artifactId, parameterId, data);
      
      const response = await apiClient.put(`/artifacts/${artifactId}/parameters/${parameterId}`, data);
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to update parameter:', error);
      throw error;
    }
  }

  static async deleteParameter(artifactId: string, parameterId: string): Promise<void> {
    try {
      console.log('🗑️ [ArtifactService] Deleting parameter:', artifactId, parameterId);
      
      await apiClient.delete(`/artifacts/${artifactId}/parameters/${parameterId}`);
      console.log('✅ [ArtifactService] Deleted parameter:', parameterId);
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to delete parameter:', error);
      throw error;
    }
  }

  static async search(searchTerm: string): Promise<EnhancedArtifact[]> {
    try {
      console.log('🔍 [ArtifactService] Searching artifacts:', searchTerm);
      
      const artifacts = await this.getArtifacts();
      return artifacts.filter(artifact => 
        artifact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        artifact.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to search artifacts:', error);
      return [];
    }
  }

  /**
   * Helper method to filter trees by company ID
   * ISSUE-066 fix: Extracted for reuse with cached data
   */
  private static filterTreesByCompany(trees: any[], companyId?: string, accessibleCompanyIds?: string[]): any[] {
    if (!companyId || !Array.isArray(trees)) {
      return trees;
    }
    
    // Build list of valid company IDs to match against
    const validCompanyIds = new Set<string>();
    validCompanyIds.add(companyId.toLowerCase());
    
    if (accessibleCompanyIds && accessibleCompanyIds.length > 0) {
      accessibleCompanyIds.forEach(id => validCompanyIds.add(id.toLowerCase()));
    }
    
    return trees.filter((tree: any) => {
      let treeCompanyId: string | null = null;
      
      if (tree.company_id) {
        treeCompanyId = String(tree.company_id).toLowerCase();
      } else if (tree.tree_metadata) {
        const metadataCompanyId = tree.tree_metadata.company_id || 
                                 (tree.tree_metadata.tree_metadata && tree.tree_metadata.tree_metadata.company_id);
        if (metadataCompanyId) {
          treeCompanyId = String(metadataCompanyId).toLowerCase();
        }
      }
      
      if (!treeCompanyId) {
        return false;
      }
      
      return validCompanyIds.has(treeCompanyId);
    });
  }

  /**
   * Get artifact trees
   * ISSUE-066 fix: Uses request deduplication and response caching to prevent redundant API calls
   */
  static async getTrees(companyId?: string, accessibleCompanyIds?: string[]): Promise<any[]> {
    const params = new URLSearchParams();
    if (companyId) {
      params.append('company_id', companyId);
    }
    
    const queryString = params.toString();
    const url = `/artifacts/trees${queryString ? `?${queryString}` : ''}`;
    const cacheKey = `trees:${url}`;
    
    // Check cache first (ISSUE-066 fix)
    const cached = responseCache.get(cacheKey);
    if (cached) {
      if (process.env.NODE_ENV === 'development') {
        console.log('📦 [ArtifactService] Using cached trees:', { companyId, treeCount: cached.length });
      }
      // Still need to apply client-side filtering to cached data
      return this.filterTreesByCompany(cached, companyId, accessibleCompanyIds);
    }
    
    // Use request deduplication (ISSUE-066 fix)
    const requestKey = `GET:${url}`;
    
    try {
      const response = await requestDeduplicator.deduplicate(requestKey, async () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 [ArtifactService] Fetching artifact trees', companyId ? `for company: ${companyId}` : '');
          console.log('📡 [ArtifactService] Calling endpoint:', url);
        }
        
        const apiResponse = await apiClient.get(url);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('📥 [ArtifactService] Response received:', {
            status: apiResponse.status,
            dataCount: Array.isArray(apiResponse.data) ? apiResponse.data.length : 'not an array',
            data: apiResponse.data
          });
        }
        
        return apiResponse;
      });
      
      // Cache the raw response data (ISSUE-066 fix)
      const trees = response.data || [];
      responseCache.set(cacheKey, trees);
      
      // Apply client-side filtering using helper method
      const filteredTrees = this.filterTreesByCompany(trees, companyId, accessibleCompanyIds);
      
      if (process.env.NODE_ENV === 'development' && companyId) {
        console.log(`🔍 [ArtifactService] Filtered ${filteredTrees.length} trees from ${trees.length} total (companyId: ${companyId})`);
      }
      
      return filteredTrees;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to fetch trees:', error);
      return [];
    }
  }

  /**
   * Invalidate tree-related caches (call after mutations)
   * ISSUE-066 fix: Clear cached data after create/update/delete
   */
  private static invalidateTreeCaches(): void {
    // Clear all tree-related cache entries
    responseCache.clear();
    if (process.env.NODE_ENV === 'development') {
      console.log('🗑️ [ArtifactService] Cache invalidated after tree mutation');
    }
  }

  static async createTree(name: string, description?: string, tree_metadata?: any): Promise<any> {
    try {
      console.log('🔄 [ArtifactService] Creating artifact tree:', name);
      
      const response = await apiClient.post('/artifacts/trees', { name, description, tree_metadata });
      console.log('✅ [ArtifactService] Created tree:', response.data.name);
      
      // Invalidate cache after mutation (ISSUE-066 fix)
      this.invalidateTreeCaches();
      
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to create tree:', error);
      throw error;
    }
  }

  static async updateTree(treeId: string, data: { name?: string; description?: string; company_id?: string | null; tree_metadata?: any }): Promise<any> {
    try {
      console.log('🔄 [ArtifactService] Updating artifact tree:', treeId);
      console.log('📤 [ArtifactService] Update payload:', JSON.stringify(data, null, 2));
      
      const response = await apiClient.put(`/artifacts/trees/${treeId}`, data);
      console.log('✅ [ArtifactService] Updated tree:', response.data.name);
      console.log('📥 [ArtifactService] Response data:', {
        id: response.data.id,
        name: response.data.name,
        company_id: response.data.company_id,
        tree_metadata: response.data.tree_metadata
      });
      
      // Invalidate cache after mutation (ISSUE-066 fix)
      this.invalidateTreeCaches();
      
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to update tree:', error);
      throw error;
    }
  }

  static async deleteTree(treeId: string, deleteArtifacts: boolean = false): Promise<void> {
    try {
      console.log('🔄 [ArtifactService] Deleting artifact tree:', treeId, { deleteArtifacts });
      
      // Use the correct endpoint: /api/v1/artifacts/trees/{tree_id}
      // Support delete_artifacts query parameter
      const params = new URLSearchParams();
      if (deleteArtifacts) {
        params.append('delete_artifacts', 'true');
      }
      
      const url = `/artifacts/trees/${treeId}${params.toString() ? `?${params.toString()}` : ''}`;
      await apiClient.delete(url);
      console.log('✅ [ArtifactService] Deleted tree:', treeId);
      
      // Invalidate cache after mutation (ISSUE-066 fix)
      this.invalidateTreeCaches();
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to delete tree:', error);
      throw error;
    }
  }

  static async getRootStatus(treeId: string, companyId?: string): Promise<any> {
    try {
      console.log('🔄 [ArtifactService] Getting root status for tree:', treeId);
      
      // Use the same hierarchy endpoint as getHierarchy to check if there's a root artifact
      const params = new URLSearchParams();
      if (companyId) params.append('company_id', companyId);
      if (treeId) params.append('tree_id', treeId);
      
      const url = `/artifacts/hierarchy${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get(url);
      
      // Response format: { hierarchy: [...], total_count: number, root_count: number }
      const hierarchy = response.data.hierarchy || [];
      
      const hasRoot = hierarchy && hierarchy.length > 0;
      const rootId = hasRoot ? hierarchy[0].id : null;
      
      console.log('✅ [ArtifactService] Root status:', { hasRoot, rootId, hierarchyLength: hierarchy.length });
      
      return {
        hasRoot,
        rootId
      };
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to get root status:', error);
      throw error;
    }
  }

  static async updateState(artifactId: string, state: any): Promise<any> {
    try {
      console.log('🔄 [ArtifactService] Updating state for artifact:', artifactId, state);
      
      // If state has an ID, update it; otherwise create a new one
      if (state.id) {
        const response = await apiClient.put(`/artifacts/${artifactId}/states/${state.id}`, state);
        return response.data;
      } else {
        const response = await apiClient.post(`/artifacts/${artifactId}/states`, state);
        return response.data;
      }
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to update state:', error);
      throw error;
    }
  }

  // Artifact Items Management
  static async getArtifactItems(artifactId: string): Promise<any[]> {
    try {
      console.log('🔄 [ArtifactService] Fetching artifact items for artifact:', artifactId);
      
      const response = await apiClient.get(`/artifacts/${artifactId}/items`);
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.items)) {
        return response.data.items;
      } else if (response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      
      return [];
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to fetch artifact items:', error);
      return [];
    }
  }

  static async createArtifactItem(
    artifact_id: string,
    initial_state_id: string,
    item_data: Record<string, unknown>,
    item_name?: string,
    description?: string
  ): Promise<any> {
    try {
      console.log('🔄 [ArtifactService] Creating artifact item:', { artifact_id, initial_state_id, item_name });
      
      const requestBody: {
        artifact_id: string;
        initial_state_id: string;
        item_data: Record<string, unknown>;
        item_name?: string;
        description?: string;
      } = {
        artifact_id,
        initial_state_id,
        item_data,
      };

      if (item_name) {
        requestBody.item_name = item_name;
      }
      if (description) {
        requestBody.description = description;
      }

      const response = await apiClient.post('/artifact-items', requestBody);
      console.log('✅ [ArtifactService] Created artifact item:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [ArtifactService] Failed to create artifact item:', error);
      throw error;
    }
  }

  static async updateArtifactItemState(itemId: string, stateId: string): Promise<any> {
    try {
      console.log('🔄 [ArtifactService] Updating artifact item state:', { itemId, stateId });
      
      // Use query parameter for state_id as per backend endpoint
      const response = await apiClient.put(`/artifact-items/${itemId}/state?state_id=${stateId}`);
      console.log('✅ [ArtifactService] Updated artifact item state');
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to update artifact item state:', error);
      throw error;
    }
  }

  // Artifact Properties Management
  private static pendingGetProperties: Map<string, Promise<any[]>> = new Map();

  static async getArtifactProperties(artifactId: string): Promise<any[]> {
    // If there's already a pending request for this artifactId, return it
    if (this.pendingGetProperties.has(artifactId)) {
      return this.pendingGetProperties.get(artifactId)!;
    }

    // Create a new request
    const requestPromise = (async (): Promise<any[]> => {
      try {
        console.log('🔄 [ArtifactService] Fetching artifact properties for artifact:', artifactId);
        
        const response = await apiClient.get(`/artifacts/${artifactId}/properties`);
        
        // Handle different response formats
        if (Array.isArray(response.data)) {
          return response.data;
        } else if (response.data && Array.isArray(response.data.properties)) {
          return response.data.properties;
        } else if (response.data && Array.isArray(response.data.items)) {
          return response.data.items;
        } else if (response.data && Array.isArray(response.data.data)) {
          return response.data.data;
        }
        
        return [];
      } catch (error: any) {
        // 404 is expected when artifact has no properties - don't log as error
        if (error.response?.status === 404) {
          console.log('ℹ️ [ArtifactService] No properties found for artifact (this is normal):', artifactId);
          return [];
        }
        
        // Log other errors
        console.error('❌ [ArtifactService] Failed to fetch artifact properties:', error);
        return [];
      }
    })().finally(() => {
      // Clean up after request completes
      this.pendingGetProperties.delete(artifactId);
    });

    this.pendingGetProperties.set(artifactId, requestPromise);
    return requestPromise;
  }

  // Apply Prime Components to Company Artifact
  static async applyPrimeProperties(artifactId: string): Promise<any> {
    try {
      console.log('🔄 [ArtifactService] Applying prime properties to artifact:', artifactId);
      const response = await apiClient.post(`/artifacts/${artifactId}/apply-prime-properties`);
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to apply prime properties:', error);
      throw error;
    }
  }

  static async applyPrimeProcesses(artifactId: string): Promise<any> {
    try {
      console.log('🔄 [ArtifactService] Applying prime processes to artifact:', artifactId);
      const response = await apiClient.post(`/artifacts/${artifactId}/apply-prime-processes`);
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to apply prime processes:', error);
      throw error;
    }
  }

  static async applyPrimeAll(artifactId: string): Promise<any> {
    try {
      console.log('🔄 [ArtifactService] Applying all prime components to artifact:', artifactId);
      const response = await apiClient.post(`/artifacts/${artifactId}/apply-prime-all`);
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to apply all prime components:', error);
      throw error;
    }
  }

  /**
   * Get artifact items requiring user input
   * ISSUE-004: Endpoint to retrieve artifact items that are on transitions requiring user input
   */
  static async getItemsRequiringUserInput(
    userId: string,
    filters?: {
      companyId?: string;
      artifactTreeId?: string;
      priority?: 'high' | 'medium' | 'low';
      includeProperties?: boolean;
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    items: any[];
    total: number;
    prioritized_count: number;
  }> {
    try {
      console.log('🔄 [ArtifactService] Fetching items requiring user input:', { userId, filters });
      
      const params = new URLSearchParams({
        user_id: userId,
      });
      
      if (filters?.companyId) {
        params.append('company_id', filters.companyId);
      }
      if (filters?.artifactTreeId) {
        params.append('artifact_tree_id', filters.artifactTreeId);
      }
      if (filters?.priority) {
        params.append('priority', filters.priority);
      }
      if (filters?.includeProperties !== undefined) {
        params.append('include_properties', filters.includeProperties.toString());
      }
      if (filters?.limit !== undefined) {
        params.append('limit', filters.limit.toString());
      }
      if (filters?.offset !== undefined) {
        params.append('offset', filters.offset.toString());
      }
      
      const response = await apiClient.get(`/artifact-items/requiring-user-input?${params.toString()}`);
      console.log('✅ [ArtifactService] Fetched items requiring user input:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to fetch items requiring user input:', error);
      throw error;
    }
  }

  /**
   * Get global visibility properties
   * ISSUE-005: Endpoint to retrieve properties marked as global visibility
   */
  static async getGlobalVisibilityProperties(
    companyId: string,
    filters?: {
      artifactTypes?: string[];
      propertyKeys?: string[];
    }
  ): Promise<{
    properties: Record<string, {
      value: string | number;
      artifact_id: string | null;
      artifact_name: string;
      artifact_item_id: string | null;
      item_name: string | null;
      updated_at: string | null;
    }>;
    last_updated: string;
  }> {
    try {
      console.log('🔄 [ArtifactService] Fetching global visibility properties:', { companyId, filters });
      
      const params = new URLSearchParams({
        company_id: companyId,
      });
      
      // Add artifact types (array parameter)
      if (filters?.artifactTypes && filters.artifactTypes.length > 0) {
        filters.artifactTypes.forEach((type) => {
          params.append('artifact_types', type);
        });
      }
      
      // Add property keys (array parameter)
      if (filters?.propertyKeys && filters.propertyKeys.length > 0) {
        filters.propertyKeys.forEach((key) => {
          params.append('property_keys', key);
        });
      }
      
      const response = await apiClient.get(`/artifact-items/global-properties?${params.toString()}`);
      console.log('✅ [ArtifactService] Fetched global visibility properties:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [ArtifactService] Failed to fetch global visibility properties:', error);
      throw error;
    }
  }
}

export const artifactService = new ArtifactService();
export { ArtifactService };